import React, { useState, useEffect } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";
import api from "../../utils/api";
import {
  FaList,
  FaCalendarAlt,
  FaArrowDown,
  FaArrowUp,
  FaGamepad,
  FaExchangeAlt,
  FaSearch,
  FaChevronLeft,
  FaChevronRight,
  FaFilter,
  FaTimes,
} from "react-icons/fa";

const TransactionHistory = () => {
  const navigate = useNavigate();

  // Data State
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter State
  const [filterType, setFilterType] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [dateRange, setDateRange] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Pagination State
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [pageInput, setPageInput] = useState(1);

  // Debounce Search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1); // Reset page on new search
    }, 800);
    return () => clearTimeout(timer);
  }, [search]);

  // Sync pageInput with page
  useEffect(() => {
    setPageInput(page);
  }, [page]);

  // Helper: Format Date as YYYY-MM-DD in Local Time
  const formatDateLocal = (date) => {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  // Handle Date Range Presets
  const handleDateRangeChange = (e) => {
    const range = e.target.value;
    setDateRange(range);
    setPage(1); // Reset page

    const today = new Date();
    const todayStr = formatDateLocal(today);

    if (range === "all") {
      setDateFrom("");
      setDateTo("");
    } else if (range === "today") {
      setDateFrom(todayStr);
      setDateTo(todayStr);
    } else if (range === "yesterday") {
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      setDateFrom(formatDateLocal(yesterday));
      setDateTo(formatDateLocal(yesterday));
    } else if (range === "week") {
      const lastWeek = new Date(today);
      lastWeek.setDate(today.getDate() - 7);
      setDateFrom(formatDateLocal(lastWeek));
      setDateTo(todayStr);
    } else if (range === "month") {
      const lastMonth = new Date(today);
      lastMonth.setDate(today.getDate() - 30);
      setDateFrom(formatDateLocal(lastMonth));
      setDateTo(todayStr);
    } else if (range === "custom") {
      // Do not clear, let user edit
    }
  };

  // Fetch Transactions
  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      try {
        // Construct params object for cleaner API calls
        const params = {
          page,
          limit,
          type: filterType || undefined,
          status: filterStatus || undefined,
          startDate: dateFrom || undefined,
          endDate: dateTo || undefined,
          search: debouncedSearch || undefined,
        };

        const { data } = await api.get("/transactions/me", { params });

        if (data.success) {
          setTransactions(data.transactions);
          setTotalPages(data.pagination.pages);
          setTotalTransactions(data.pagination.total);
        }
      } catch (error) {
        console.error("Failed to fetch transactions", error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [
    page,
    limit,
    filterType,
    filterStatus,
    dateFrom,
    dateTo,
    debouncedSearch,
  ]);

  // Pagination Handlers
  const handlePageInput = (e) => {
    const val = e.target.value;
    // Allow empty string to let user delete content
    if (val === "") {
      setPageInput("");
      return;
    }
    // Only allow positive integers
    if (/^\d+$/.test(val)) {
      setPageInput(val);
    }
  };

  // Debounce Page Input
  useEffect(() => {
    // If empty or invalid, do nothing yet
    if (pageInput === "" || isNaN(parseInt(pageInput))) return;

    const timer = setTimeout(() => {
      let numVal = parseInt(pageInput);

      // Clamp value between 1 and totalPages
      const clampedVal = Math.max(1, Math.min(numVal, totalPages));

      if (clampedVal !== page) {
        setPage(clampedVal);
      }

      // If the user typed a value out of bounds (e.g. 999 where max is 10),
      // auto-correct the input display to the max value
      if (String(clampedVal) !== String(pageInput)) {
        setPageInput(clampedVal);
      }
    }, 800);

    return () => clearTimeout(timer);
  }, [pageInput, totalPages, page]);

  const getTransactionIcon = (type, action) => {
    if (type === "deposit")
      return (
        <div className="rounded-circle bg-success bg-opacity-10 p-2 text-success">
          <FaArrowDown />
        </div>
      );
    if (type === "withdraw")
      return (
        <div className="rounded-circle bg-danger bg-opacity-10 p-2 text-danger">
          <FaArrowUp />
        </div>
      );
    if (type === "win")
      return (
        <div className="rounded-circle bg-success bg-opacity-10 p-2 text-success">
          <FaArrowUp />
        </div>
      );
    if (type === "bet")
      return (
        <div className="rounded-circle bg-secondary bg-opacity-10 p-2 text-secondary">
          <FaGamepad />
        </div>
      );
    return (
      <div className="rounded-circle bg-primary bg-opacity-10 p-2 text-primary">
        <FaExchangeAlt />
      </div>
    );
  };

  // UI State
  const [showFilters, setShowFilters] = useState(false);

  return (
    <div className="container mt-4 mb-5">
      <div className="card shadow-sm border-0 overflow-hidden">
        {/* Header: Title + Search + Filter Toggle */}
        <div className="card-header bg-white py-3 d-flex flex-wrap justify-content-between align-items-center gap-3">
          <h5 className="mb-0 fw-bold d-flex align-items-center gap-2">
            <FaList className="text-primary" />
            Transaction History
          </h5>

          <div className="d-flex gap-2 align-items-center">
            <div
              className="input-group input-group-sm search-group"
              style={{ width: "250px" }}
            >
              <span className="input-group-text text-muted bg-transparent border-0">
                <FaSearch />
              </span>
              <input
                type="text"
                className="form-control text-dark bg-transparent border-0 shadow-none ps-0"
                placeholder="Search ID..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <span
                  className="input-group-text bg-transparent border-0 text-muted"
                  style={{ cursor: "pointer" }}
                  onClick={() => setSearch("")}
                >
                  <FaTimes />
                </span>
              )}
            </div>
            <button
              className={`btn btn-sm d-flex align-items-center gap-2 ${
                showFilters ? "btn-primary" : "btn-outline-primary"
              }`}
              onClick={() => setShowFilters(!showFilters)}
              style={{ height: "31px" }} // Explicit height to match input-group-sm
            >
              <FaFilter /> <span className="d-none d-sm-inline">Filters</span>
            </button>
          </div>
        </div>

        {/* Collapsible Filter Section */}
        {showFilters && (
          <div className="bg-light p-3 border-bottom animate-slide-down">
            <div className="row g-3">
              {/* Type Filter */}
              <div className="col-lg-3 col-md-4 col-sm-6 col-12">
                <label className="form-label small text-muted fw-bold text-uppercase mb-1">
                  Type
                </label>
                <select
                  className="form-select bg-white text-dark"
                  value={filterType}
                  onChange={(e) => {
                    setPage(1);
                    setFilterType(e.target.value);
                  }}
                >
                  <option value="">All Types</option>
                  <option value="deposit">Deposit</option>
                  <option value="withdraw">Withdraw</option>
                  <option value="bet">Bet</option>
                  <option value="win">Win</option>
                  <option value="bonus">Bonus</option>
                </select>
              </div>

              {/* Status Filter */}
              <div className="col-lg-3 col-md-4 col-sm-6 col-12">
                <label className="form-label small text-muted fw-bold text-uppercase mb-1">
                  Status
                </label>
                <select
                  className="form-select bg-white text-dark"
                  value={filterStatus}
                  onChange={(e) => {
                    setPage(1);
                    setFilterStatus(e.target.value);
                  }}
                >
                  <option value="">All Statuses</option>
                  <option value="success">Success</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              </div>

              {/* Time Period */}
              <div className="col-lg-3 col-md-4 col-sm-6 col-12">
                <label className="form-label small text-muted fw-bold text-uppercase mb-1">
                  Time Period
                </label>
                <select
                  className="form-select bg-white text-dark"
                  value={dateRange}
                  onChange={handleDateRangeChange}
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="yesterday">Yesterday</option>
                  <option value="week">Since Last Week</option>
                  <option value="month">Last 30 Days</option>
                  <option value="custom">Custom Range</option>
                </select>
              </div>

              {/* Reset Button */}
              <div className="col-lg-3 col-md-12 col-sm-6 col-12 d-flex align-items-end">
                <button
                  className="btn btn-outline-danger w-100 bg-white"
                  onClick={() => {
                    setFilterType("");
                    setFilterStatus("");
                    setDateRange("all");
                    setDateFrom("");
                    setDateTo("");
                    setSearch("");
                    setPage(1);
                  }}
                >
                  Reset
                </button>
              </div>
            </div>

            {/* Custom Date Range */}
            {dateRange === "custom" && (
              <div className="row mt-3">
                <div className="col-lg-4 col-md-6 col-12 ms-lg-auto">
                  <div className="input-group input-group-sm">
                    <input
                      type="date"
                      className="form-control bg-white text-dark"
                      value={dateFrom}
                      onChange={(e) => {
                        setPage(1);
                        setDateFrom(e.target.value);
                      }}
                    />
                    <span className="input-group-text bg-light text-muted">
                      to
                    </span>
                    <input
                      type="date"
                      className="form-control bg-white text-dark"
                      value={dateTo}
                      onChange={(e) => {
                        setPage(1);
                        setDateTo(e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
              <p className="mt-2 text-muted">Loading transactions...</p>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-5">
              <div className="display-1 text-muted opacity-25 mb-3">
                <FaList />
              </div>
              <p className="text-muted">No transactions found.</p>
            </div>
          ) : (
            <div className="list-group list-group-flush">
              {transactions.map((tx) => {
                const isPositive = tx.action === "credit";
                return (
                  <div
                    key={tx._id}
                    className="list-group-item p-3 hover-bg-light transition-all"
                    style={{
                      borderLeft: isPositive
                        ? "4px solid #198754"
                        : "4px solid #dc3545",
                    }}
                  >
                    <div className="d-flex gap-3">
                      {/* Icon */}
                      <div className="flex-shrink-0">
                        {getTransactionIcon(tx.actionType, tx.action)}
                      </div>

                      {/* Content */}
                      <div className="flex-grow-1 w-100">
                        {/* Title + Amount */}
                        <div className="d-flex flex-column flex-sm-row justify-content-between">
                          <span className="fw-bold text-dark text-break">
                            {tx.description || tx.actionType}
                          </span>

                          <span
                            className={`fw-bold ${
                              isPositive ? "text-success" : "text-danger"
                            }`}
                          >
                            {isPositive ? "" : "-"}{tx.currency === "INR" ? "₹" : tx.currency || "₹"}{tx.amount?.toLocaleString("en-IN")}
                          </span>
                        </div>

                        {/* Date + Balance */}
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mt-1">
                          <div className="text-muted small">
                            <FaCalendarAlt className="me-1" size={12} />
                            {new Date(tx.createdAt).toLocaleString("en-IN", {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </div>

                          <div className="text-muted small">
                            Bal:
                            <span className="fw-medium ms-1">
                              {tx.currency === "INR" ? "₹" : tx.currency || "₹"}{tx.balanceAfter?.toLocaleString("en-IN")}
                            </span>
                          </div>
                        </div>

                        {/* ID + Status */}
                        <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center mt-2 gap-1">
                          <span className="badge bg-light text-secondary border font-monospace text-break">
                            ID: {tx.transactionId}
                          </span>

                          <span
                            className={`badge rounded-pill ${
                              tx.status === "success"
                                ? "bg-success"
                                : tx.status === "pending"
                                ? "bg-warning text-dark"
                                : "bg-danger"
                            } px-3`}
                            style={{
                              fontWeight: "500",
                              textTransform: "capitalize",
                              width: "fit-content",
                            }}
                          >
                            {tx.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination - Casino Style */}
          {!loading && transactions.length > 0 && (
            <div className="p-3 bg-light border-top d-flex flex-wrap justify-content-between align-items-center gap-3">
              <div className="d-flex align-items-center gap-3">
                <div className="text-muted small fw-bold">
                  Total: {totalTransactions} entries
                </div>
                <div className="d-flex align-items-center gap-2">
                  <select
                    className="form-select form-select-sm"
                    style={{ width: "auto" }}
                    value={limit}
                    onChange={(e) => {
                      setPage(1);
                      setLimit(parseInt(e.target.value));
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-muted small">per page</span>
                </div>
              </div>

              <div className="d-flex align-items-center gap-3">
                <button
                  className="btn btn-outline-secondary btn-sm border-0"
                  onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                  disabled={page <= 1}
                >
                  <FaChevronLeft />
                </button>

                <div className="d-flex align-items-center gap-2">
                  <input
                    type="number"
                    className="form-control form-control-sm text-center fw-bold text-primary border-primary"
                    style={{ width: "60px" }}
                    value={pageInput}
                    onChange={handlePageInput}
                  />
                  <span className="text-muted fw-bold">/ {totalPages}</span>
                </div>

                <button
                  className="btn btn-outline-secondary btn-sm border-0"
                  onClick={() =>
                    setPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={page >= totalPages}
                >
                  <FaChevronRight />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`
                /* Container-based Seamless Search - Fixes borders */
                .search-group {
                    border: 1px solid #ced4da;
                    border-radius: 0.25rem;
                    background-color: #fff;
                    transition: all 0.2s;
                }
                .search-group:focus-within {
                    border-color: #86b7fe;
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
                }
                .search-group .input-group-text {
                    background-color: transparent !important;
                    border: none !important;
                    color: #6c757d;
                }
                .search-group .form-control {
                    background-color: transparent !important;
                    border: none !important;
                    box-shadow: none !important;
                }
                /* Highlight Icon on Focus */
                .search-group:focus-within .input-group-text {
                    color: #0d6efd !important;
                }

                .hover-bg-light:hover { background-color: #f8f9fa; }
                .list-group-item { transition: background-color 0.2s; }
                
                /* Hide scrollbar for number input */
                input[type=number]::-webkit-inner-spin-button, 
                input[type=number]::-webkit-outer-spin-button { 
                  -webkit-appearance: none; 
                  margin: 0; 
                }
                
                /* Force Light Inputs */
                .form-control, .form-select {
                    background-color: #fff !important;
                    color: #000 !important;
                    border: 1px solid #ced4da;
                }
                .form-control:focus, .form-select:focus {
                    background-color: #fff !important;
                    color: #000 !important;
                    border-color: #86b7fe;
                    box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
                }
                
                /* Fix Date Input specific icons if needed (browser dependent) */
                input[type="date"]::-webkit-calendar-picker-indicator {
                    filter: invert(0); /* Force dark icon */
                }
            `}</style>
    </div>
  );
};

export default TransactionHistory;
